import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import { CheckboxList } from "components/CheckboxList";
import CountryDropdown from "components/dropdowns/reactselect/CountryDropdown";
import StateDropdown from "components/dropdowns/reactselect/StateDropdown";
import { StatusDropdown } from "components";
import UrlInput from "components/urlinput";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import React, { useCallback } from "react";
import { findCountryById, findStateById } from "utilities/countrystateutils";
import CompanyNameField from "./CompanyNameField";
import { useCustomQuery } from "utilities/hookutils";
import { useDebounce } from "utilities/usehooks";
import { Message } from "semantic-ui-react";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import { getCountryFormDefinition } from "CMS/utils";
import { useOpenSidePanel } from "../SidePanel";
import { useOpenArtistSidePanel, useOpenCompanySidePanel, useOpenPromoterSidePanel } from "./CompanySidePanel";
import { useOpenVenueSidePanel } from "../Venue/VenueSidePanel";
import TagsSelect from "components/Tag/TagsSelect";
import { AfterCreateAction } from "../types";

interface NewCompanyFormState {
  companyName: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  country: server.dto.Country;
  state: server.dto.State;
  euVatNumber: string;
  fiscalNumber1: string;
  phoneNumber: string;
  website: string;
  status: server.dto.StatusSmall;
  isArtist: boolean;
  isVenue: boolean;
  isPromoter: boolean;
  capacity: number;
  googlePlaceId?: string;
  latLng: server.dto.Geo;
  tags: server.dto.TagSmall[];
}

type NewCompanyType = "artist" | "venue" | "promoter" | "company";

interface Props {
  type: NewCompanyType;
  disableArtist?: boolean;
  disableVenue?: boolean;
  disablePromoter?: boolean;
  onCreated: (company: server.dto.Company) => void;
  afterCreate: AfterCreateAction;
}

const NewCompanySidePanel = (props: Props) => {
  const initialCountry = serverReference.countries.find(c => c.key === site.userPreference.regional.country);
  const [formState, setFormState] = React.useState<NewCompanyFormState>({
    companyName: "",
    addressLine1: "",
    addressLine2: "",
    postalCode: "",
    city: "",
    country: initialCountry,
    state: initialCountry.states.find(s => s.isCountryDefault),
    euVatNumber: "",
    fiscalNumber1: "",
    phoneNumber: "",
    website: "",
    status: site.currentEnvironment.statuses.company[0],
    isArtist: props.type === "artist",
    isVenue: props.type === "venue",
    isPromoter: props.type === "promoter",
    capacity: undefined,
    latLng: undefined,
    tags: [],
  });
  const openArtistSidePanel = useOpenArtistSidePanel();
  const openVenueSidePanel = useOpenVenueSidePanel();
  const openPromoterSidePanel = useOpenPromoterSidePanel();
  const openCompanySidePanel = useOpenCompanySidePanel();

  const onChange = (newData: Partial<NewCompanyFormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newData,
    }));
  }

  const onGooglePlaceSelected = (place: server.dto.GooglePlace) => {
    onChange({
      companyName: place.name ?? "",
      addressLine1: place.address.addressLine1 ?? "",
      addressLine2: place.address.addressLine2 ?? "",
      postalCode: place.address.postalCode ?? "",
      city: place.address.city ?? "",
      country: findCountryById(place.address.country) ?? undefined,
      state: findStateById(place.address.stateId) ?? undefined,
      website: truncateUrl(place.website ?? ""),
      googlePlaceId: place.placeIdentifier,
      latLng: place.latLng
    });
  }

  const onNorwegianCompanySelected = (data: server.dto.NorwegianCompanyRegistration) => {
    onChange({
      companyName: data.name ?? "",
      addressLine1: data.address?.addressLine1 ?? "",
      addressLine2: data.address?.addressLine2 ?? "",
      postalCode: data.address?.postalCode ?? "",
      city: data.address?.city ?? "",
      country: data.address ? findCountryById(data.address.country) : findCountryById("norway"),
      state: data.address ? findStateById(data.address.stateId) : findCountryById("norway").states.find(s => s.isCountryDefault),
      website: truncateUrl(data.website ?? ""),
    })
  }

  const onSuccess = async (companyId: number) => {
    const res = await window.CoreApi.Company.GetSidePanelData(companyId).then(r => r.data);
    props.onCreated(res.data.company);
  }

  const submit = () => {
    return window.CoreApi.Company.Create({
      googlePlaceId: formState.googlePlaceId,
      isArtist: formState.isArtist ? "active" : "no",
      isPromoter: formState.isPromoter ? "active" : "no",
      isVenue: formState.isVenue ? "active" : "no",
      regularAddress: {
        addressLine1: formState.addressLine1,
        addressLine2: formState.addressLine2,
        postalCode: formState.postalCode,
        city: formState.city,
        stateId: formState.state?.key
      },
      regularName: formState.companyName,
      fiscalNumber1: formDefinition.fiscalNumber1.displayInUI ? formState.fiscalNumber1 : undefined,
      euVatNumber: formDefinition.euVatNumber.displayInUI ? formState.euVatNumber : undefined,
      phoneNumbers: convertToPhoneNumbers(formState.phoneNumber),
      website: formState.website,
      statusId: formState.status.statusId,
      venueCapacity: formState.isVenue ? formState.capacity : undefined,
      geoLocation: formState.latLng,
      tagsIds: formState.tags.map(t => t.tagId)
    });
  };

  const debouncedCompanyName = useDebounce(formState.companyName, 500);
  const companyExistsQuery = useCustomQuery({
    queryKey: ["companyExists", debouncedCompanyName],
    queryFn: () => window.CoreApi.Company.NameExists({ name: debouncedCompanyName }).then(r => r.data),
    enabled: !!debouncedCompanyName,
  });

  const formDefinition = getCountryFormDefinition(formState.country.key);

  return <SidePanelNewSectionTemplate
    onSave={(id) => {
      onSuccess(id);
      if (props.afterCreate === "displayItemInSidebar") {
        switch (props.type) {
          case "artist":
            openArtistSidePanel(id); return;
          case "venue":
            openVenueSidePanel(id); return;
          case "promoter":
            openPromoterSidePanel(id); return;
          case "company":
            openCompanySidePanel(id); return;
        }
      }
    }}
    afterCreate={props.afterCreate}
    title={site.currentEnvironment.company.newLabel}
    id="new-company-side-panel"
    trigger={null}
    submit={submit}
    fields={[
      {
        key: "name",
        label: RESX.GeneralLabels.Name,
        renderEditNew: () => (
          <CompanyNameField
            country={formState.country}
            name={formState.companyName}
            onChange={(newName) => onChange({ companyName: newName })}
            onGooglePlaceSelected={onGooglePlaceSelected}
            onNorwegianCompanySelected={onNorwegianCompanySelected}
          />
        )
      }, {
        key: "addressLine1",
        label: RESX.Address.AddressLine + " 1",
        renderEditNew: () => (
          <OptionalTextInput
            maxLength={serverReference.constants.address.maxAddressLineLength}
            value={formState.addressLine1}
            onChange={(value) => onChange({ addressLine1: value })}
          />
        )
      },
      {
        key: "addressLine2",
        label: RESX.Address.AddressLine + " 2",
        renderEditNew: () => (
          <OptionalTextInput
            maxLength={serverReference.constants.address.maxAddressLineLength}
            value={formState.addressLine2}
            onChange={(value) => onChange({ addressLine2: value })}
          />
        )
      }, {
        key: "postalCode",
        label: RESX.Address.PostalCode,
        renderEditNew: () => (
          <OptionalTextInput
            maxLength={serverReference.constants.address.maxPostalCodeLength}
            value={formState.postalCode}
            onChange={(value) => onChange({ postalCode: value })}
          />
        )
      }, {
        key: "city",
        label: RESX.Address.City,
        renderEditNew: () => (
          <OptionalTextInput
            maxLength={serverReference.constants.address.maxCityLength}
            value={formState.city}
            onChange={(value) => onChange({ city: value })}
          />
        )
      }, {
        key: "country",
        label: RESX.Address.Country,
        renderEditNew: () => (
          <CountryDropdown
            value={formState.country}
            onChange={(value) => onChange({ country: value, state: value.states.find(s => s.isCountryDefault) })}
          />
        )
      }, {
        key: "state",
        label: RESX.Address.State,
        renderEditNew: () => (
          <StateDropdown
            value={formState.state}
            options={formState.country?.states ?? []}
            onChange={(value) => onChange({ state: value })}
          />
        )
      }, {
        key: "euVatNumber",
        label: formDefinition.euVatNumber.label,
        sublabel: formDefinition.euVatNumber.sublabel,
        hidden: !formDefinition.euVatNumber.displayInUI,
        renderEditNew: () => (
          <OptionalTextInput
            maxLength={serverReference.constants.company.maxEuVatNumberLength}
            value={formState.euVatNumber}
            onChange={(value) => onChange({ euVatNumber: value })}
          />
        )
      }, {
        key: "fiscalNumber1",
        label: formDefinition.fiscalNumber1.label,
        sublabel: formDefinition.fiscalNumber1.sublabel,
        hidden: !formDefinition.fiscalNumber1.displayInUI,
        renderEditNew: () => (
          <OptionalTextInput
            maxLength={serverReference.constants.company.maxFiscalNumber1Length}
            value={formState.fiscalNumber1}
            onChange={(value) => onChange({ fiscalNumber1: value })}
          />
        )
      }, {
        key: "phoneNumber",
        label: RESX.Company.Phone,
        renderEditNew: () => (
          <PhoneInput
            value={formState.phoneNumber}
            onChange={(value) => onChange({ phoneNumber: value })}
          />
        )
      }, {
        key: "website",
        label: RESX.GeneralLabels.Website,
        renderEditNew: () => (
          <UrlInput
            validatorKey="website"
            value={formState.website}
            onValueChange={(value) => onChange({ website: value })}
            maxLength={serverReference.constants.url.maxUrlLength}
          />
        )
      }, {
        key: "status",
        label: RESX.Status.resxStatus,
        hidden: site.currentEnvironment.statuses.company.length <= 1,
        renderEditNew: () => (
          <StatusDropdown
            mainArea="company"
            value={formState.status}
            options={undefined}
            onChange={(value) => onChange({ status: value })}
          />
        )
      },
      {
        key: "profile",
        label: RESX.Company.Profile,
        renderEditNew: () => (
          <ProfileCheckboxList
            isArtist={formState.isArtist}
            isVenue={formState.isVenue}
            isPromoter={formState.isPromoter}
            disableArtist={props.disableArtist}
            disableVenue={props.disableVenue}
            disablePromoter={props.disablePromoter}
            onChange={onChange}
          />
        )
      }, {
        key: "capacity",
        label: RESX.Venue.Capacity,
        hidden: !formState.isVenue,
        renderEditNew: () => (
          <OptionalNumberInput
            value={formState.capacity}
            maxValue={serverReference.constants.venue.maxCapacity}
            labelContent="PAX"
            onChange={(value) => onChange({ capacity: value })}
            onEnterPressed={value => onChange({ capacity: value })}
            decimalPoints={0}
            canBeEmpty
            canBeNegative={false}
            width={100}
          />
        )
      }, {
        key: "tags",
        label: RESX.Tag.Tags,
        renderEditNew: () => <TagsSelect
          mainArea="company"
          value={formState.tags}
          onChange={(tags) => onChange({ tags })}
          inPageEditing="new"
        />
      }, {
        key: "companyExistsWarning",
        label: "",
        hideLabel: true,
        hidden: !companyExistsQuery.data?.data,
        renderEditNew: () => <Message color="red" className="margin-top margin-bottom">
          {RESX.Company.ACompanyWithThisNameAlreadyExists}
        </Message>
      }
    ]}
  />
}

type ProfileKey = "artist" | "venue" | "promoter";

interface ProfileCheckboxListProps {
  isArtist: boolean;
  isVenue: boolean;
  isPromoter: boolean;
  disableArtist?: boolean;
  disableVenue?: boolean;
  disablePromoter?: boolean;
  onChange: (value: { isArtist: boolean; isVenue: boolean; isPromoter: boolean }) => void;
}

const ProfileCheckboxList = (props: ProfileCheckboxListProps) => {
  const fg = site.currentEnvironment.functionGroups;

  const options: { key: ProfileKey; label: string; disabled?: boolean }[] = [];
  if (fg.artist.enabled) options.push({ key: "artist", label: fg.artist.singular, disabled: props.disableArtist });
  if (fg.venue.enabled) options.push({ key: "venue", label: fg.venue.singular, disabled: props.disableVenue });
  if (fg.promoter.enabled) options.push({ key: "promoter", label: fg.promoter.singular, disabled: props.disablePromoter });

  const selectedKeys = new Set<ProfileKey>();
  if (props.isArtist) selectedKeys.add("artist");
  if (props.isVenue) selectedKeys.add("venue");
  if (props.isPromoter) selectedKeys.add("promoter");
  const value = options.filter(o => selectedKeys.has(o.key));

  return <CheckboxList
    options={options}
    getId={o => o.key}
    getLabel={o => o.label}
    getDisabled={o => o.disabled}
    value={value}
    onChange={(selected) => {
      const keys = new Set(selected.map(s => s.key));
      props.onChange({
        isArtist: keys.has("artist"),
        isVenue: keys.has("venue"),
        isPromoter: keys.has("promoter"),
      });
    }}
    multipleColumns={false}
    inPageEditing="new"
    keyboardNavigationGroup="newCompanyProfile"
  />;
};

export const useOpenNewCompanySidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openNewCompanySidePanel = useCallback((props: Props) => {
    openSidePanel(
      <NewCompanySidePanel {...props} />,
      "new-company-side-panel",
      "large"
    );
  }, [openSidePanel]);

  return openNewCompanySidePanel;
}

const truncateUrl = (input: string) => {
  if (input == null) return "";

  const maxLength = serverReference.constants.url.maxUrlLength;
  // If the URL is short enough, return it as is
  if (input.length <= maxLength) return input;

  try {
    const url = new URL(input);

    // First remove the query params, see if it fits within the max length
    const withPath = url.origin + url.pathname;
    if (withPath.length <= maxLength) {
      return withPath;
    }

    // Otherwise remove the path & query params, just get the origin like "https://www.example.com"    
    return url.origin;
  } catch {
    // If it's not a valid URL, just return an empty string
    return "";
  }
}